import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { booksAPI, productsAPI, paymentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { 
    BookOpen, 
    ChevronRight, 
    Clock, 
    Loader2,
    Plus,
    FileText,
    Check,
    Lock,
    ShoppingCart,
    Plane,
    Star,
    Download
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// Ebook PDF URL
const EBOOK_PDF_URL = "https://customer-assets.emergentagent.com/job_read-and-connect-1/artifacts/ijytkpwf_WEBSITE%20BOOK%20HOW%20TO%20BECOME%20A%20FLIGHT%20ATTENDAND%20%2B%20ACE%20THE%20INTERVIEW.pdf";

// Payhip purchase link for ebook
const PAYHIP_EBOOK_URL = "https://payhip.com/b/zshap";

// Square payment link (backup)
const SQUARE_EBOOK_URL = "https://square.link/u/QjLKh0eM";

export default function Book() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [book, setBook] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [previewChapters, setPreviewChapters] = useState([]);
    const [bookProduct, setBookProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(false);
    const [showAddBook, setShowAddBook] = useState(false);
    const [showAddChapter, setShowAddChapter] = useState(false);
    const [bookForm, setBookForm] = useState({ 
        title: 'Your Dream Job: Flight Attendant Interview Success', 
        author: 'Shelly Perreault', 
        description: 'The complete guide to launching your cabin crew career. Learn insider secrets, interview tips, and everything you need to land your dream job as a flight attendant.', 
        cover_image: 'https://customer-assets.emergentagent.com/job_ff86ad6e-6b25-4a43-8693-36fd1b19af83/artifacts/1ir7o3bl_IMG_2671.jpeg' 
    });
    const [chapterForm, setChapterForm] = useState({ title: '', content: '', order: 1 });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadBook();
    }, [user]);

    const loadBook = async () => {
        try {
            const [booksRes, productRes] = await Promise.all([
                booksAPI.getAll(),
                productsAPI.getBookInfo()
            ]);
            
            setBookProduct(productRes.data);
            
            if (booksRes.data.length > 0) {
                const firstBook = booksRes.data[0];
                setBook(firstBook);
                
                // Get preview chapters (available to all)
                try {
                    const previewRes = await booksAPI.getChaptersPreview(firstBook.id);
                    setPreviewChapters(previewRes.data);
                } catch (err) {
                    console.log('No preview chapters');
                }
                
                // Get all chapters if user has access
                if (user?.has_book_access) {
                    try {
                        const chaptersRes = await booksAPI.getChapters(firstBook.id);
                        setChapters(chaptersRes.data);
                    } catch (err) {
                        console.log('Could not load chapters');
                    }
                }
            }
        } catch (err) {
            console.error('Failed to load book:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = () => {
        // Redirect directly to Payhip for instant ebook purchase
        window.open(PAYHIP_EBOOK_URL, '_blank');
    };

    const handleCreateBook = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error('Please login to create a book');
            return;
        }
        setSubmitting(true);
        try {
            const res = await booksAPI.create(bookForm);
            setBook(res.data);
            setShowAddBook(false);
            toast.success('Book created successfully!');
        } catch (err) {
            toast.error('Failed to create book');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateChapter = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error('Please login to add a chapter');
            return;
        }
        setSubmitting(true);
        try {
            const res = await booksAPI.createChapter(book.id, {
                ...chapterForm,
                order: chapters.length + 1
            });
            setChapters([...chapters, res.data]);
            setShowAddChapter(false);
            toast.success('Chapter added successfully!');
            setChapterForm({ title: '', content: '', order: 1 });
        } catch (err) {
            toast.error('Failed to add chapter');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!book) {
        return (
            <div className="container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-2xl mx-auto text-center"
                >
                    <div className="h-24 w-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                        <BookOpen className="h-12 w-12 text-muted-foreground" />
                    </div>
                    <h1 className="font-heading text-3xl font-bold mb-4">Book Coming Soon</h1>
                    <p className="text-muted-foreground mb-8">
                        The book is being prepared. {user ? 'You can add it now.' : 'Please check back later.'}
                    </p>
                    
                    {user && (
                        <Dialog open={showAddBook} onOpenChange={setShowAddBook}>
                            <DialogTrigger asChild>
                                <Button className="rounded-full gap-2" data-testid="add-book-btn">
                                    <Plus className="h-4 w-4" />
                                    Add Your Book
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Add Your Book</DialogTitle>
                                    <DialogDescription>
                                        Create your book to start adding chapters
                                    </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={handleCreateBook} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="book-title">Title</Label>
                                        <Input
                                            id="book-title"
                                            value={bookForm.title}
                                            onChange={(e) => setBookForm({ ...bookForm, title: e.target.value })}
                                            required
                                            data-testid="book-title-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="book-author">Author</Label>
                                        <Input
                                            id="book-author"
                                            value={bookForm.author}
                                            onChange={(e) => setBookForm({ ...bookForm, author: e.target.value })}
                                            required
                                            data-testid="book-author-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="book-description">Description</Label>
                                        <Textarea
                                            id="book-description"
                                            value={bookForm.description}
                                            onChange={(e) => setBookForm({ ...bookForm, description: e.target.value })}
                                            rows={3}
                                            required
                                            data-testid="book-description-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="book-cover">Cover Image URL (optional)</Label>
                                        <Input
                                            id="book-cover"
                                            value={bookForm.cover_image}
                                            onChange={(e) => setBookForm({ ...bookForm, cover_image: e.target.value })}
                                            placeholder="https://..."
                                            data-testid="book-cover-input"
                                        />
                                    </div>
                                    <Button type="submit" className="w-full rounded-full" disabled={submitting} data-testid="submit-book-btn">
                                        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                        Create Book
                                    </Button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </motion.div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="grid lg:grid-cols-[350px_1fr] gap-8">
                {/* Sidebar - Book Info */}
                <motion.aside
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                >
                    {/* Book Card */}
                    <Card className="overflow-hidden">
                        <div className="aspect-[3/4] relative bg-gradient-to-br from-primary/20 to-primary/5">
                            <img
                                src="https://customer-assets.emergentagent.com/job_ff86ad6e-6b25-4a43-8693-36fd1b19af83/artifacts/1ir7o3bl_IMG_2671.jpeg"
                                alt={book.title}
                                className="object-cover w-full h-full"
                            />
                            {user?.has_book_access && (
                                <div className="absolute top-4 right-4">
                                    <Badge className="bg-green-500">
                                        <Check className="h-3 w-3 mr-1" />
                                        Owned
                                    </Badge>
                                </div>
                            )}
                        </div>
                        <div className="p-4">
                            <h2 className="font-heading text-xl font-semibold mb-1">{book.title}</h2>
                            <p className="text-muted-foreground text-sm mb-3">by {book.author}</p>
                            <div className="flex items-center gap-1 mb-3">
                                {[1,2,3,4,5].map(i => (
                                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                ))}
                                <a 
                                    href="/reviews" 
                                    className="text-sm text-primary hover:underline ml-2 cursor-pointer"
                                    data-testid="book-reviews-link"
                                >
                                    (778 reviews)
                                </a>
                            </div>
                            <p className="text-sm line-clamp-3 mb-4">{book.description}</p>
                            
                            {!user?.has_book_access ? (
                                <div className="space-y-3">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-3xl font-bold text-primary">
                                            $27
                                        </span>
                                        <Badge className="bg-green-500 text-white text-xs ml-2">
                                            INSTANT DOWNLOAD
                                        </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Get instant access after purchase via Payhip
                                    </p>
                                    <Button 
                                        className="w-full rounded-full gap-2"
                                        onClick={handlePurchase}
                                        data-testid="purchase-book-btn"
                                    >
                                        <ShoppingCart className="h-4 w-4" />
                                        Purchase Ebook - $27
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <Badge variant="secondary" className="w-full justify-center py-2">
                                        <Check className="h-4 w-4 mr-2" />
                                        You own this ebook
                                    </Badge>
                                    <Button 
                                        className="w-full rounded-full gap-2 bg-green-600 hover:bg-green-700"
                                        onClick={() => window.open(EBOOK_PDF_URL, '_blank')}
                                        data-testid="download-ebook-btn"
                                    >
                                        <Download className="h-4 w-4" />
                                        Download PDF
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Chapters List (for owners) */}
                    {user?.has_book_access && chapters.length > 0 && (
                        <Card className="p-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold">Chapters</h3>
                                <Badge variant="secondary">{chapters.length}</Badge>
                            </div>
                            
                            <ScrollArea className="h-[300px]">
                                <div className="space-y-1">
                                    {chapters.map((chapter, index) => (
                                        <button
                                            key={chapter.id}
                                            onClick={() => navigate(`/book/chapter/${chapter.id}`)}
                                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                                            data-testid={`chapter-${index + 1}`}
                                        >
                                            <span className="text-xs font-mono text-muted-foreground w-6">
                                                {String(index + 1).padStart(2, '0')}
                                            </span>
                                            <span className="text-sm font-medium flex-1 truncate">
                                                {chapter.title}
                                            </span>
                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                        </button>
                                    ))}
                                </div>
                            </ScrollArea>

                            {user && (
                                <>
                                    <Separator className="my-4" />
                                    <Dialog open={showAddChapter} onOpenChange={setShowAddChapter}>
                                        <DialogTrigger asChild>
                                            <Button variant="outline" className="w-full rounded-full gap-2" data-testid="add-chapter-btn">
                                                <Plus className="h-4 w-4" />
                                                Add Chapter
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                            <DialogHeader>
                                                <DialogTitle>Add New Chapter</DialogTitle>
                                                <DialogDescription>
                                                    Add a new chapter to your book
                                                </DialogDescription>
                                            </DialogHeader>
                                            <form onSubmit={handleCreateChapter} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="chapter-title">Chapter Title</Label>
                                                    <Input
                                                        id="chapter-title"
                                                        value={chapterForm.title}
                                                        onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })}
                                                        required
                                                        data-testid="chapter-title-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="chapter-content">Content</Label>
                                                    <Textarea
                                                        id="chapter-content"
                                                        value={chapterForm.content}
                                                        onChange={(e) => setChapterForm({ ...chapterForm, content: e.target.value })}
                                                        rows={15}
                                                        required
                                                        placeholder="Write your chapter content here..."
                                                        data-testid="chapter-content-input"
                                                    />
                                                </div>
                                                <Button type="submit" className="w-full rounded-full" disabled={submitting} data-testid="submit-chapter-btn">
                                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                                    Add Chapter
                                                </Button>
                                            </form>
                                        </DialogContent>
                                    </Dialog>
                                </>
                            )}
                        </Card>
                    )}
                </motion.aside>

                {/* Main Content */}
                <motion.main
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <Card className="p-6 md:p-8">
                        <div className="flex items-center gap-2 mb-6">
                            <Plane className="h-5 w-5 text-primary" />
                            <span className="text-sm text-muted-foreground">Flight Attendant Career Guide</span>
                            <Badge variant="outline" className="ml-auto">
                                <Download className="h-3 w-3 mr-1" />
                                Instant PDF Download
                            </Badge>
                        </div>
                        
                        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                            {book.title}
                        </h1>
                        
                        <p className="text-lg text-muted-foreground mb-8">{book.description}</p>
                        
                        {/* What You'll Learn */}
                        <div className="mb-8">
                            <h2 className="font-heading text-xl font-semibold mb-4">What You'll Learn</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {[
                                    'How to prepare your application and resume',
                                    'What airlines look for in candidates',
                                    'Interview preparation and common questions',
                                    'Group assessment tips and strategies',
                                    'Physical requirements and grooming standards',
                                    'Day in the life of a flight attendant',
                                    'Salary expectations and career growth',
                                    'Top airlines and how to apply',
                                    'Airport Code tricks to remember',
                                    'Popular airline questions they always ask',
                                    'How to give the right answer to situational questions',
                                    'Tricks on how to always have an answer to any airline interview questions'
                                ].map((item, i) => (
                                    <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                                        <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                                        <span className="text-sm">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CTA */}
                        {!user?.has_book_access ? (
                            <Card className="p-6 bg-primary/5 border-primary/20">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <h3 className="font-heading text-lg font-semibold mb-1">
                                            Ready to Start Your Journey?
                                        </h3>
                                        <p className="text-muted-foreground text-sm">
                                            Get instant PDF download for just ${bookProduct?.price || '29.99'}
                                        </p>
                                    </div>
                                    <Button 
                                        size="lg"
                                        className="rounded-full gap-2"
                                        onClick={handlePurchase}
                                        disabled={purchasing}
                                        data-testid="cta-purchase-book"
                                    >
                                        {purchasing ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <ShoppingCart className="h-4 w-4" />
                                        )}
                                        Purchase Ebook
                                    </Button>
                                </div>
                            </Card>
                        ) : (
                            <Card className="p-6 bg-green-50 border-green-200">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <h3 className="font-heading text-lg font-semibold mb-1 text-green-800">
                                            You Own This Ebook!
                                        </h3>
                                        <p className="text-green-700 text-sm">
                                            Download your PDF and start learning today
                                        </p>
                                    </div>
                                    <Button 
                                        size="lg"
                                        className="rounded-full gap-2 bg-green-600 hover:bg-green-700"
                                        onClick={() => window.open(EBOOK_PDF_URL, '_blank')}
                                        data-testid="cta-download-ebook"
                                    >
                                        <Download className="h-5 w-5" />
                                        Download PDF
                                    </Button>
                                </div>
                            </Card>
                        )}

                        {/* Book Reviews */}
                        <div className="mt-12">
                            <h2 className="font-heading text-2xl font-semibold mb-6">Reader Reviews</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {[
                                    {
                                        name: "Sarah M.",
                                        role: "Hired at Delta Airlines",
                                        text: "This book was my bible during the application process! Every tip was spot-on. The interview chapter alone is worth 10x the price. I got hired on my first try!",
                                        rating: 5
                                    },
                                    {
                                        name: "Michelle K.",
                                        role: "Hired at Southwest Airlines",
                                        text: "The book covers everything from resume tips to what to wear. Shelly's insider knowledge of what airlines really look for gave me such an advantage.",
                                        rating: 5
                                    },
                                    {
                                        name: "Taylor B.",
                                        role: "Hired at Spirit Airlines",
                                        text: "I read this book three times before my interview. The section on common interview questions and how to structure your answers was incredibly helpful!",
                                        rating: 5
                                    },
                                    {
                                        name: "Christina L.",
                                        role: "Hired at Allegiant Air",
                                        text: "Shelly breaks down the entire hiring process in a way that's easy to understand. I felt so prepared walking into my interview. Highly recommend!",
                                        rating: 5
                                    }
                                ].map((review, i) => (
                                    <Card key={review.name} className="p-4">
                                        <div className="flex gap-1 mb-2">
                                            {[...Array(review.rating)].map((_, i) => (
                                                <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                            ))}
                                        </div>
                                        <p className="text-sm text-muted-foreground mb-3">"{review.text}"</p>
                                        <div>
                                            <p className="font-medium text-sm">{review.name}</p>
                                            <p className="text-xs text-muted-foreground">{review.role}</p>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </Card>
                </motion.main>
            </div>
        </div>
    );
}
